'use client';

import useScrollToView from '@repo/hooks/dom/useScrollToView';
import { startTransition, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import type { BestOfAwardsConfigResult } from '../BestOfAwards/types';
import { ProductListData } from '../LinkedDataJson/ProductListData';
import { isMultiProductBannerEnabledForSlug } from '../roundups/MultiProductBanner/utils';
import { summaryTableRecipe } from './summaryTable.recipe';
import SummaryTableContainer from './SummaryTableContainer';
import { SummaryTableDatadogTiming } from './SummaryTableDatadogTiming';
import SummaryTableHead from './SummaryTableHead';
import SummaryTableMobileHead from './SummaryTableMobileHead';
import SummaryTableRow from './SummaryTableRow';
import { SkeletonRow } from './SummaryTableRow/SkeletonRow';
import { SummaryTableSeeMoreAccountsButton } from './SummaryTableSeeMoreAccountsButton';
import { type SummaryTableRowWithDrivers } from './types';
import { getHeadings } from './utils';

const MULTI_PRODUCT_BANNER_ROWS_TO_SHOW = 4;

type SummaryTableProps = {
  getBestOfAwardsConfig?: (
    offerId: string,
    institutionId?: string,
    subProductType?: string | null,
  ) => BestOfAwardsConfigResult | undefined;
  isFilterActive?: boolean;
  pageUrl: string;
  rows: SummaryTableRowWithDrivers[];
  shouldApplyStylingUpdates?: boolean;
  showMobileLayout?: boolean;
  showSkeleton?: boolean;
  suppressApyTermPrefix?: boolean;
  maxRows?: number;
  numTruncatedRows?: number;
  allowTruncatingRows?: boolean;
  slug?: string;
  allowTruncationWithFilters?: boolean;
};

export default function SummaryTable({
  allowTruncationWithFilters,
  rows,
  shouldApplyStylingUpdates = false,
  showMobileLayout = false,
  showSkeleton = false,
  getBestOfAwardsConfig,
  pageUrl,
  suppressApyTermPrefix = false,
  maxRows,
  numTruncatedRows,
  allowTruncatingRows,
  slug,
  isFilterActive,
}: SummaryTableProps) {
  const isMultiProductBannerEnabled = isMultiProductBannerEnabledForSlug(slug ?? '');

  const rowsToShow = isMultiProductBannerEnabled
    ? MULTI_PRODUCT_BANNER_ROWS_TO_SHOW
    : numTruncatedRows;

  const [rowsAreTruncated, setRowsAreTruncated] = useState(true);

  const truncationEnabled =
    (isMultiProductBannerEnabled || (allowTruncatingRows && maxRows && rows.length > maxRows)) &&
    (!isFilterActive || allowTruncationWithFilters);
  const visibleRows =
    truncationEnabled && rowsAreTruncated && rowsToShow != null ? rows.slice(0, rowsToShow) : rows;

  const hasMoreRowsThanVisibleCap = rowsToShow != null && rows.length > rowsToShow;
  const showTruncateButton = truncationEnabled && (hasMoreRowsThanVisibleCap || !rowsAreTruncated);

  const viewMoreLessButtonHandler = () => {
    startTransition(() => {
      setRowsAreTruncated(t => !t);
    });

    if (!rowsAreTruncated) {
      useScrollToView('summary-table-headers', 'smooth', 0);
    }
  };

  if (!rows?.length) {
    return null;
  }

  const styles = summaryTableRecipe({ showMobileLayout });
  const headings = getHeadings(rows[0].marketplaceEntity);

  const tableContent = visibleRows.map((row, index) =>
    showSkeleton ? (
      <SkeletonRow
        key={`skeleton-${index}`}
        driversLength={row.marketplaceEntity.drivers.length}
        showMobileLayout={showMobileLayout}
      />
    ) : (
      <SummaryTableRow
        key={row.marketplaceEntity.id}
        index={index}
        row={row}
        bestOfAwardsConfig={getBestOfAwardsConfig?.(
          row.marketplaceEntity.offerForCta?.id ?? row.marketplaceEntity.id,
          row.marketplaceEntity.institution?.id,
          row.marketplaceEntity.subProductType,
        )}
        shouldApplyStylingUpdates={shouldApplyStylingUpdates}
        showMobileLayout={showMobileLayout}
        suppressApyTermPrefix={suppressApyTermPrefix}
      />
    ),
  );

  const seeMoreLabel = rowsAreTruncated ? 'See more accounts' : 'See less accounts';
  const SeeMoreIcon = rowsAreTruncated ? MdExpandMore : MdExpandLess;

  return (
    <>
      <SummaryTableContainer className={styles.container}>
        <table className={styles.table}>
          <thead className={styles.thead} id="summary-table-headers">
            {showMobileLayout && <SummaryTableMobileHead className={styles.mobileHeadRow} />}
            <SummaryTableHead
              headings={headings}
              shouldApplyStylingUpdates={shouldApplyStylingUpdates}
              className={showMobileLayout ? styles.desktopHeadRow : undefined}
            />
          </thead>
          <tbody className={styles.tbody}>
            {showSkeleton ? (
              tableContent
            ) : (
              <SummaryTableDatadogTiming>{tableContent}</SummaryTableDatadogTiming>
            )}
          </tbody>
        </table>
      </SummaryTableContainer>
      {showTruncateButton && (
        <SummaryTableSeeMoreAccountsButton
          label={seeMoreLabel}
          Icon={SeeMoreIcon}
          onClick={viewMoreLessButtonHandler}
        />
      )}
      <ProductListData
        id="product-list-linked-data"
        offers={visibleRows}
        url={pageUrl}
      />
    </>
  );
}
